
import { Check, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  painelButtonPrimaryClass,
  painelPermissionCounterClass,
  painelPermissionItemDisabledClass,
  painelPermissionItemEnabledClass,
} from '@/components/painel/painel-styles';
import { PainelRocketScroll } from '@/components/painel/PainelRocketScroll';
import {
  PainelPermissionsDialogShell,
  PainelRoleIcon,
  type RoleVisualConfig,
} from '@/components/painel/permissions/painel-role-visuals';
import {
  PAINEL_ALL_PERMISSIONS,
  PAINEL_PERMISSION_GROUPS,
  PAINEL_PERMISSION_LABELS,
  type PainelPermission,
} from '@/lib/painel-rbac';
import { cn } from '@/lib/utils';

type PainelPermissionsEditorModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle: string;
  visual: RoleVisualConfig;
  enabledPermissions: PainelPermission[];
  readOnly?: boolean;
  onSave?: (permissions: PainelPermission[]) => void;
};

export function PainelPermissionsEditorModal({
  open,
  onOpenChange,
  title,
  subtitle,
  visual,
  enabledPermissions,
  readOnly = true,
  onSave,
}: PainelPermissionsEditorModalProps) {
  const enabledSet = new Set(enabledPermissions);
  const activeCount = enabledPermissions.length;
  const totalCount = PAINEL_ALL_PERMISSIONS.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-h-[90vh] max-w-2xl overflow-hidden border-0 bg-[#030023] p-0 shadow-none"
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <PainelPermissionsDialogShell>
          <div className="border-b border-white/10 bg-blue-950/20 px-6 pb-5 pt-6 sm:px-8">
            <div className="flex items-start gap-4 pr-14">
              <PainelRoleIcon config={visual} size="lg" />
              <div className="min-w-0 flex-1">
                <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/55">
                  Editar permissões
                </p>
                <h2 className="mt-1 font-poppins text-xl font-bold text-white sm:text-2xl">{title}</h2>
                <p className="mt-1.5 font-inter text-sm text-slate-400">{subtitle}</p>
              </div>
              <div
                className={cn(
                  'hidden shrink-0 px-3 py-2 text-right sm:block',
                  painelPermissionCounterClass
                )}
              >
                <p className="font-poppins text-lg font-bold leading-none text-white">
                  {activeCount}
                  <span className="text-sm font-normal text-slate-500">/{totalCount}</span>
                </p>
                <p className="mt-0.5 font-inter text-[10px] uppercase tracking-wider text-sky-300/70">
                  ativas
                </p>
              </div>
            </div>
          </div>

          <PainelRocketScroll
            className='max-h-[min(52vh,480px)] bg-[#030023]/30'
            viewportClassName='px-6 py-5 sm:px-8 scroll-smooth'
          >
            <div className="space-y-6">
              {PAINEL_PERMISSION_GROUPS.map((group) => (
                <section key={group.id}>
                  <div className="mb-3 flex items-center gap-2">
                    <Shield className="h-4 w-4 text-sky-400/70" aria-hidden />
                    <h3 className="font-poppins text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                      {group.label}
                    </h3>
                    <span className="ml-auto font-inter text-[11px] text-slate-500">
                      {group.permissions.filter((p) => enabledSet.has(p)).length}/{group.permissions.length}
                    </span>
                  </div>
                  <ul className="grid gap-2 sm:grid-cols-2">
                    {group.permissions.map((permission) => {
                      const enabled = enabledSet.has(permission);
                      return (
                        <li
                          key={permission}
                          className={cn(
                            'flex items-center gap-3 rounded-xl border px-3.5 py-2.5 transition-colors',
                            enabled
                              ? painelPermissionItemEnabledClass
                              : painelPermissionItemDisabledClass
                          )}
                        >
                          <span
                            className={cn(
                              'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border',
                              enabled
                                ? 'border-sky-400/50 bg-sky-500/25 text-sky-100'
                                : 'border-white/10 bg-white/[0.03] text-transparent'
                            )}
                          >
                            <Check className="h-3.5 w-3.5" aria-hidden />
                          </span>
                          <span className="font-inter text-[13px] text-blue-50">
                            {PAINEL_PERMISSION_LABELS[permission]}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          </PainelRocketScroll>

          <div className="flex flex-col-reverse gap-2 border-t border-white/10 bg-blue-950/25 px-6 py-4 sm:flex-row sm:justify-end sm:px-8">
            <Button
              type="button"
              variant="ghost"
              className="h-10 rounded-xl text-slate-400 hover:bg-white/10 hover:text-white"
              onClick={() => onOpenChange(false)}
            >
              Fechar
            </Button>
            {readOnly ? (
              <Button
                type="button"
                disabled
                className={cn(painelButtonPrimaryClass, 'h-10 px-5 opacity-60')}
              >
                Salvar alterações
              </Button>
            ) : onSave ? (
              <Button
                type="button"
                className={cn(painelButtonPrimaryClass, 'h-10 px-5')}
                onClick={() => {
                  onSave(enabledPermissions);
                  onOpenChange(false);
                }}
              >
                Salvar alterações
              </Button>
            ) : null}
          </div>
        </PainelPermissionsDialogShell>
      </DialogContent>
    </Dialog>
  );
}
